import { useMemo, useState } from 'react'
import { List, type RowComponentProps } from 'react-window'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox'
import SearchIcon from '@mui/icons-material/Search'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import Divider from '@mui/material/Divider'
import InputAdornment from '@mui/material/InputAdornment'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Popover from '@mui/material/Popover'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {
  getGridStringOperators,
  type GridFilterInputValueProps,
  type GridFilterItem,
  type GridFilterOperator,
} from '@mui/x-data-grid-premium'
import OverflowTooltip from 'components/UI/OverflowTooltip/OverflowTooltip'
import colors from 'lib/colors'

export type LookupFilterOption = {
  key: string
  label: string
}

export type LookupFilterGroup = {
  title: string
  keys: string[]
}

type RowProps = {
  menuRows: LookupMenuRow[]
}

type LookupMenuRow =
  | { type: 'bulk'; key: string; label: string; keys: string[] }
  | { type: 'group'; key: string; title: string }
  | { type: 'option'; key: string; option: LookupFilterOption }
  | { type: 'empty'; key: string }

type BuildLookupFilterOperatorsParams = {
  lookup: Record<string, string>
  groups?: LookupFilterGroup[]
  searchable?: boolean
  showBulkToggles?: boolean
}

const MENU_HEIGHT = 360
const GROUP_ROW_HEIGHT = 28
const EMPTY_ROW_HEIGHT = 48
const OPTION_ROW_HEIGHT = 32

function sortByLabel<T extends { label: string }>(items: T[]) {
  return [...items].sort((a, b) => a.label.localeCompare(b.label))
}

function getToggleState(keys: string[], selectedKeySet: Set<string>) {
  const selectedCount = keys.filter((key) => selectedKeySet.has(key)).length

  return {
    checked: selectedCount > 0 && selectedCount === keys.length,
    indeterminate: selectedCount > 0 && selectedCount < keys.length,
  }
}

function getRowHeight(row: LookupMenuRow) {
  switch (row.type) {
    case 'group':
      return GROUP_ROW_HEIGHT
    case 'empty':
      return EMPTY_ROW_HEIGHT
    default:
      return OPTION_ROW_HEIGHT
  }
}

export default function buildLookupFilterOperators({
  lookup,
  groups,
  searchable = true,
  showBulkToggles = true,
}: BuildLookupFilterOperatorsParams): GridFilterOperator[] {
  const allOptions = sortByLabel(
    Object.entries(lookup).map(([key, label]) => ({ key, label }))
  )
  const allOptionsByKey = new Map(allOptions.map((option) => [option.key, option]))

  const sortedGroups = groups
    ?.map((group) => ({
      ...group,
      options: sortByLabel(
        group.keys
          .map((key) => allOptionsByKey.get(key))
          .filter((option): option is LookupFilterOption => Boolean(option))
      ),
    }))
    .sort((a, b) => a.title.localeCompare(b.title))

  function LookupFilterInput({ item, applyValue }: GridFilterInputValueProps) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [search, setSearch] = useState('')

    const selectedKeys = useMemo(
      () => (Array.isArray(item.value) ? item.value.map(String) : []),
      [item.value]
    )

    const selectedKeySet = useMemo(
      () => new Set(selectedKeys),
      [selectedKeys]
    )

    const normalizedSearch = search.trim().toLowerCase()

    const visibleOptions = useMemo(() => {
      if (!normalizedSearch) return allOptions

      return allOptions.filter((option) =>
        option.label.toLowerCase().includes(normalizedSearch)
      )
    }, [normalizedSearch])

    const visibleGroups = useMemo(() => {
      if (!sortedGroups) return undefined

      return sortedGroups
        .map((group) => ({
          ...group,
          options: group.options.filter((option) =>
            option.label.toLowerCase().includes(normalizedSearch)
          ),
        }))
        .filter((group) => group.options.length > 0)
    }, [normalizedSearch])

    const visibleKeys = useMemo(
      () =>
        (visibleGroups ?? [{ options: visibleOptions }]).flatMap((group) =>
          group.options.map((option) => option.key)
        ),
      [visibleGroups, visibleOptions]
    )

    const open = Boolean(anchorEl)
    const bulkLabel = normalizedSearch ? 'Show/Hide Visible' : 'Show/Hide All'

    const menuRows = useMemo<LookupMenuRow[]>(() => {
      if (visibleKeys.length === 0) {
        return [{ type: 'empty', key: 'empty' }]
      }

      const rows: LookupMenuRow[] = []

      if (showBulkToggles) {
        rows.push({
          type: 'bulk',
          key: 'bulk-visible',
          label: bulkLabel,
          keys: visibleKeys,
        })
      }

      if (visibleGroups) {
        visibleGroups.forEach((group) => {
          rows.push({
            type: 'group',
            key: `group-${group.title}`,
            title: group.title,
          })

          if (showBulkToggles) {
            rows.push({
              type: 'bulk',
              key: `bulk-${group.title}`,
              label: bulkLabel,
              keys: group.options.map((option) => option.key),
            })
          }

          group.options.forEach((option) => {
            rows.push({
              type: 'option',
              key: `option-${option.key}`,
              option,
            })
          })
        })
      } else {
        visibleOptions.forEach((option) => {
          rows.push({
            type: 'option',
            key: `option-${option.key}`,
            option,
          })
        })
      }

      return rows
    }, [bulkLabel, visibleGroups, visibleKeys, visibleOptions])

    const listHeight = Math.min(
      MENU_HEIGHT,
      menuRows.reduce((height, row) => height + getRowHeight(row), 0)
    )

    function updateSelectedKeys(nextSelectedKeys: string[]) {
      applyValue({
        ...item,
        value: nextSelectedKeys,
      } as GridFilterItem)
    }

    function toggleKey(key: string) {
      if (selectedKeySet.has(key)) {
        updateSelectedKeys(selectedKeys.filter((selectedKey) => selectedKey !== key))
      } else {
        updateSelectedKeys([...selectedKeys, key])
      }
    }

    function toggleKeys(keys: string[]) {
      const { checked } = getToggleState(keys, selectedKeySet)
      const keySet = new Set(keys)

      if (checked) {
        updateSelectedKeys(selectedKeys.filter((selectedKey) => !keySet.has(selectedKey)))
      } else {
        updateSelectedKeys([...new Set([...selectedKeys, ...keys])])
      }
    }

    function Row({
      index,
      style,
      ariaAttributes,
      menuRows,
    }: RowComponentProps<RowProps>) {
      const row = menuRows[index]

      if (row.type === 'empty') {
        return (
          <Typography
            {...ariaAttributes}
            variant='body2'
            style={style}
            sx={{
              alignItems: 'center',
              color: 'text.secondary',
              display: 'flex',
              justifyContent: 'center',
              px: 3,
              textAlign: 'center',
            }}
          >
            No matching values
          </Typography>
        )
      }

      if (row.type === 'group') {
        return (
          <Typography
            {...ariaAttributes}
            variant='subtitle3'
            style={style}
            sx={{
              alignItems: 'center',
              display: 'flex',
              px: 3,
            }}
          >
            <OverflowTooltip title={row.title}>{row.title}</OverflowTooltip>
          </Typography>
        )
      }

      if (row.type === 'bulk') {
        const { checked, indeterminate } = getToggleState(row.keys, selectedKeySet)

        return (
          <MenuItem
            {...ariaAttributes}
            dense
            style={style}
            onClick={() => toggleKeys(row.keys)}
            sx={{ px: 3, gap: 1.5, minHeight: '28px' }}
          >
            <Checkbox
              checked={checked}
              indeterminate={indeterminate}
              indeterminateIcon={<IndeterminateCheckBoxIcon />}
              size='medium'
              disableRipple
              sx={{ p: 0 }}
            />
            <ListItemText primary={row.label} />
          </MenuItem>
        )
      }

      const checked = selectedKeySet.has(row.option.key)

      return (
        <MenuItem
          {...ariaAttributes}
          dense
          style={style}
          onClick={() => toggleKey(row.option.key)}
          sx={{ px: 3, gap: 1.5, minHeight: '28px', minWidth: 0 }}
        >
          <Checkbox checked={checked} size='medium' disableRipple sx={{ p: 0 }} />
          <ListItemText
            sx={{ minWidth: 0 }}
            primary={
              <OverflowTooltip title={row.option.label}>
                {row.option.label}
              </OverflowTooltip>
            }
          />
        </MenuItem>
      )
    }

    return (
      <>
        <TextField
          fullWidth
          size='small'
          label={selectedKeys.length > 0 ? 'Value' : undefined}
          value={selectedKeys.length > 0 ? '...' : ''}
          placeholder={selectedKeys.length > 0 ? undefined : 'Value'}
          onMouseDown={(event) => {
            event.preventDefault()
            setAnchorEl(event.currentTarget)
          }}
          slotProps={{
            input: {
              readOnly: true,
              endAdornment: (
                <InputAdornment position='end'>
                  <ArrowDropDownIcon />
                </InputAdornment>
              ),
            },
            inputLabel: {
              shrink: true,
            },
          }}
          sx={{
            '& .MuiInputBase-root': {
              pr: '8px',
            },
          }}
        />

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                width: 290,
                overflow: 'hidden',
                pt: 3,
                pb: 1,
              },
            },
          }}
        >
          {searchable && (
            <>
              <TextField
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder='Search'
                size='medium'
                fullWidth
                sx={{
                  px: 3,
                  mb: 1,
                  '& .MuiOutlinedInput-root': {
                    height: 40,
                  },
                  '& .MuiInputBase-input': {
                    pl: 0.5,
                  },
                  '& .MuiSvgIcon-root': {
                    color: colors.slateGray
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position='start'
                      sx={{
                        mr: 0.5,
                      }}
                    >
                      <SearchIcon fontSize='medium' />
                    </InputAdornment>
                  ),
                }}
              />
              <Divider flexItem sx={{ mx: 3, mt: 2, mb: 1 }} />
            </>
          )}

          <Box sx={{ height: listHeight }}>
            <List
              rowCount={menuRows.length}
              rowHeight={(index) => getRowHeight(menuRows[index])}
              rowProps={{ menuRows }}
              overscanCount={6}
              style={{
                height: listHeight,
                width: '100%',
              }}
              rowComponent={Row}
            />
          </Box>
        </Popover>
      </>
    )
  }

  const buildListOperator = (
    label: string,
    value: 'isAnyOf' | 'isNotAnyOf'
  ): GridFilterOperator => ({
    label,
    value,
    getApplyFilterFn: () => null,
    InputComponent: LookupFilterInput,
  })

  const textOperators = getGridStringOperators().filter((operator) =>
    ['contains', 'doesNotContain', 'startsWith', 'endsWith'].includes(operator.value)
  )

  return [
    buildListOperator('is any of', 'isAnyOf'),
    buildListOperator('is not any of', 'isNotAnyOf'),
    ...textOperators,
  ]
}
